import React, { useContext, useState } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import { Box, Button, Grid, Typography, Slider } from '@mui/material'
import { riskProfiles } from './risk-profiles'
import { useFormDebounce } from 'hooks/useFormDebounce'

export function RiskProfileAdjustment({
  onNext,
  closeModal,
  currentRiskLevel,
}) {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()

  // Store the original risk level to show as a marker
  const originalRiskLevel = intakeState?.riskAssessment?.riskProfileAdjusted
    ? intakeState?.riskAssessment?.manualRiskLevel
    : currentRiskLevel || 5

  const [selectedRiskLevel, setSelectedRiskLevel] = useState(originalRiskLevel)

  const handleSliderChange = (event, newValue) => {
    setSelectedRiskLevel(newValue)
  }

  const handleConfirm = () => {
    updateIntakeStateAndSubmit(() => {
      const newState = updateIntakeStateAtKey({
        riskAssessment: {
          ...intakeState?.riskAssessment,
          manualRiskLevel: selectedRiskLevel,
          riskProfileAdjusted: true,
        },
      })
      return newState
    })()
    if (onNext) onNext()
    if (closeModal) closeModal()
  }

  const profile = riskProfiles.find((p) => p.riskLevel === selectedRiskLevel)
  const {
    displayName,
    subtitle,
    image,
    accentHex,
    description,
    descriptionSecondary,
  } = profile || {}

  return (
    <Grid
      item
      xs={12}
      mb={3}
      sx={{ textAlign: 'left', overflow: 'hidden' }}
      px={{ xs: 0, sm: 4 }}
      pt={{ xs: 5, sm: 4 }}
      pb={{ xs: 2, sm: 4 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          width: '100%',
        }}
        mb={4}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 32,
              fontWeight: 800,
              color: '#262627',
              lineHeight: 1.167,
            }}
          >
            {displayName} Investor
          </Typography>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: accentHex,
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: '#f0f4f8',
            borderRadius: '8px',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '94.5px',
            height: '94.5px',
          }}
        >
          <img
            src={image}
            alt={displayName}
            style={{
              width: '78.5px',
              height: '78.5px',
              objectFit: 'cover',
            }}
          />
        </Box>
      </Box>

      {/* Description */}
      <Box sx={{ mb: 4 }}>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 500,
            color: '#262627',
            lineHeight: 1.5,
            mb: 1,
          }}
        >
          {description}
        </Typography>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 400,
            color: '#75787B',
            lineHeight: 1.5,
          }}
        >
          {descriptionSecondary}
        </Typography>
      </Box>

      {/* Custom Slider */}
      <Box sx={{ m: '10px', pt: 6 }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            padding: '0 12px',
          }}
        >
          {/* Current saved value marker - only show when different from selected */}
          {selectedRiskLevel !== originalRiskLevel && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: `calc(12px + (100% - 24px) * ${
                  (originalRiskLevel - 1) / 9
                })`,
                transform: 'translate(-50%, -50%)',
                width: 30,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 3,
                cursor: 'pointer',
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 18,
                  bgcolor: '#495057',
                  borderRadius: '40px',
                  marginTop: { xs: '-8px', sm: '-8px', md: '3px' },
                }}
              />
            </Box>
          )}

          {/* Slider using MUI Slider with custom styling */}
          <Slider
            value={selectedRiskLevel}
            onChange={handleSliderChange}
            min={1}
            max={10}
            step={1}
            sx={{
              width: '100%',
              height: '8px',
              padding: 0,
              '& .MuiSlider-track': {
                display: 'none', // Hide default track
              },
              '& .MuiSlider-rail': {
                backgroundColor: '#F1F1F1',
                height: 10,
                borderRadius: 5,
                opacity: 1,
              },
              '& .MuiSlider-thumb': {
                backgroundColor: 'transparent',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                border: 'none',
                boxShadow: 'none',
                '&:hover, &.Mui-focusVisible, &.Mui-active': {
                  boxShadow: 'none',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: '#2370ED',
                  borderRadius: '50%',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '30px',
                  height: '30px',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='30' height='30' viewBox='0 0 25 17.5' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.5261 4.96577L7.49157 8.00027L10.5261 11.0414L9.59186 11.9756L5.61654 8.00027L9.59186 4.02495L10.5261 4.96577Z' fill='white'/%3E%3Cpath d='M14.6165 11.0342L17.651 7.99973L14.6165 4.95861L15.5507 4.02441L19.526 7.99973L15.5507 11.9751L14.6165 11.0342Z' fill='white'/%3E%3C/svg%3E")`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                },
              },
              '& .MuiSlider-valueLabel': {
                display: 'none', // Hide default value label
              },
              '& .MuiSlider-mark': {
                display: 'none',
              },
              '& .MuiSlider-markLabel': {
                display: 'none',
              },
            }}
            marks={
              selectedRiskLevel !== originalRiskLevel
                ? [{ value: originalRiskLevel }]
                : []
            }
          />

          {/* Custom value display above the thumb */}
          <Box
            sx={{
              position: 'absolute',
              top: { xs: '-28px', sm: '-49px', md: '-36px' },
              left: `calc(12.1px + (100% - 24px) * ${
                (selectedRiskLevel - 1) / 9
              })`,
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            {/* Handle */}
            <Box
              sx={{
                width: '28px',
                height: '35px',
                backgroundImage: `url("data:image/svg+xml,%3Csvg%20width='100'%20height='127'%20viewBox='0%200%20100%20127'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3E%3Cpath%20d='M80.3132%20100.602L50%20127L19.6869%20100.602C7.17967%2089.7096%200%2073.9366%200%2057.3515V50C0%2022.3858%2022.3858%200%2050%200C77.6142%200%20100%2022.3858%20100%2050V57.3515C100%2073.9366%2092.8204%2089.7096%2080.3132%20100.602Z'%20fill='%232370ED'/%3E%3C/svg%3E")`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                position: 'relative',
              }}
            />

            {/* Number Display */}
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 18,
                fontWeight: 600,
                color: '#FFFFFF',
                lineHeight: 1,
                textAlign: 'center',
                position: 'absolute',
                top: '40%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {selectedRiskLevel}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box mt={3} display="flex" gap={2} justifyContent="flex-end">
        <Button variant="parcoRounded" onClick={handleConfirm}>
          This feels better!
        </Button>
      </Box>
    </Grid>
  )
}

export default RiskProfileAdjustment
